import { useState } from 'react';
import { Flag, LocationFlag } from '@weave/schema-gen-ts/dist/schemas/feature-flags/feature_flags_types.pb';
import { useQuery, useQueryClient } from 'react-query';
import { Icon } from '@frontend/icons';
import { useLocationService } from '@frontend/wam-location-service';
import { theme } from '@frontend/theme';
import { Table, Text, useModalControl } from '@frontend/design-system';
import { FeatureFlagTableFilters } from './feature-flag-table-filters';
import { EditFlagModal } from './modals/edit-flag-modal';
import { getLocationFeatureFlags, useSetFlagForLocationMutation } from './queries';

const MOCK_FLAG_DATA: LocationFlag[] = [
  {
    value: true,
    flag: {
      id: '61b4aff9-faf2-44f7-a1a3-4c2eced0c6d7',
      name: 'Test 1',
      description: 'Test 1 Description',
    },
  },
  {
    value: false,
    flag: {
      id: '8a45a367-5bd1-4666-9b4b-5f332e9d5ca3',
      name: 'Test 2',
      description: 'Test 2 Description',
      defaultValue: false,
    },
  },
];

export const LocationFeatureFlagTable = () => {
  const queryClient = useQueryClient();
  const { modalProps: editFlagModalProps, openModal: openEditFlagModal } = useModalControl();
  const { currentLocationId: locationId } = useLocationService();
  const [currentFeatureFlag, setCurrentFeatureFlag] = useState<Flag | undefined>(undefined);
  const { data: locationFlagData, refetch: refecthFlagData } = useQuery({
    queryKey: [locationId, 'location-feature-flags'],
    queryFn: () => getLocationFeatureFlags({ locationId }),
  });

  const { mutate: setFlagForLocation } = useSetFlagForLocationMutation({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries([locationId, 'location-feature-flags']);
        queryClient.invalidateQueries(['locations-with-flag']);
      },
    },
  });

  const flags = locationFlagData?.flags || MOCK_FLAG_DATA;

  return (
    <>
      <Table
        data={flags || []}
        colConfig={[
          {
            Header: 'Feature Flag',
            id: 'featureFlag',
            accessor: (data) => data.flag?.name,
          },
          {
            Header: 'Description',
            id: 'description',
            accessor: (data) => data.flag?.description,
            minWidth: 350,
          },
          {
            Header: 'Status',
            id: 'status',
            accessor: (data) => (data.value ? 'Show' : 'Hide'),
            cellRenderer: (data) => {
              const isShow = data === 'Show';
              return (
                <Text
                  css={{
                    backgroundColor: isShow ? theme.colors.success10 : theme.colors.critical10,
                    color: isShow ? theme.colors.success50 : theme.colors.critical50,
                    padding: theme.spacing(1, 3),
                    borderRadius: theme.borderRadius.small,
                    minWidth: 85,
                    textAlign: 'center',
                  }}
                  size='medium'
                >
                  {data}
                </Text>
              );
            },
            maxWidth: 125,
          },
          {
            Header: 'Product Owner',
            id: 'product-owner',
            cellRenderer: () => <Text>Unknown</Text>,
            maxWidth: 150,
          },
          {
            Header: 'Flag Status',
            id: 'flag-status',
            cellRenderer: () => (
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: theme.spacing(1),
                }}
              >
                <Icon name='clock-small' color='success' />
                <Text>Test</Text>
              </div>
            ),
            maxWidth: 125,
          },
          {
            Header: 'Flag Creator',
            id: 'flag-creator',
            cellRenderer: () => <Text>Unknown</Text>,
            maxWidth: 125,
          },
        ]}
        rowActions={{
          actions: [
            {
              Icon: () => <Icon name='locations-small' />,
              label: 'Update Flag Status',
              onClick: (data) => {
                setFlagForLocation({
                  locationId,
                  flagName: data.flag?.name,
                  value: !data?.value,
                });
              },
            },
            {
              Icon: () => <Icon name='edit-small' />,
              label: 'Edit Feature Flag',
              onClick: (data) => {
                setCurrentFeatureFlag(data.flag);
                openEditFlagModal();
              },
            },
            {
              Icon: () => <Icon name='plus-small' />,
              label: 'Add to Group',
              onClick: () => {
                console.log('Add to Group');
              },
            },
          ],
        }}
        isPaginated
        manualFilters
        manualFiltersRender={(modalProps, setShowNotification) => (
          <FeatureFlagTableFilters modalProps={modalProps} setShowNotificationBadge={setShowNotification} />
        )}
        hasFilterColumns
        hasGlobalSearch
        tableActions={[
          {
            Icon: () => <Icon name='update' />,
            label: 'Update',
            onClick: () => refecthFlagData(),
          },
          {
            Icon: () => <Icon name='external-link' />,
            label: 'Link',
            onClick: () => {
              console.log('Link');
            },
          },
        ]}
      ></Table>
      <EditFlagModal modalProps={editFlagModalProps} flag={currentFeatureFlag} />
    </>
  );
};

LocationFeatureFlagTable.displayName = 'LocationFeatureFlagTable';
